import React from 'react';
import { FeedbackOption } from '../../types';
import './AddReviewForm.css';

// Import local rating icons
import faceSad from '../../assets/images/face-sad.svg';
import faceSadActive from '../../assets/images/face-sad-active.svg';
import faceNeutral from '../../assets/images/face-neutral.svg';
import faceNeutralActive from '../../assets/images/face-neutral-active.svg';
import faceSmile from '../../assets/images/face-smile.svg';
import faceSmileActive from '../../assets/images/face-smile-active.svg';
import faceHappy from '../../assets/images/face-happy.svg';
import faceHappyActive from '../../assets/images/face-happy-active.svg';

interface AddReviewFormProps {
  feedbacks: FeedbackOption[];
  selectedFeedback: FeedbackOption | null;
  onSelectFeedback: (feedback: FeedbackOption) => void;
  appointmentImages: string[];
  appointmentVideos: string[];
  appointmentRecordings: string[];
  activeTab: 'Photo' | 'Video' | 'Voicemail';
  onTabChange: (tab: 'Photo' | 'Video' | 'Voicemail') => void;
  appointmentNotes: string;
  companyMessage: string;
  loading: boolean;
  onSubmit: () => void;
  onSendTip: () => void;
  onRefuseTip: () => void;
  ratingHeader: string;
}

const AddReviewForm: React.FC<AddReviewFormProps> = ({
  feedbacks,
  selectedFeedback,
  onSelectFeedback,
  companyMessage,
  loading,
  onSubmit,
  ratingHeader
}) => {

  // Map feedback icons to local assets with active states
  const getIconSrc = (feedback: FeedbackOption, isSelected: boolean) => {
    // Use the original feedback icons if they have iconActive property
    if (feedback.iconActive && isSelected) {
      return feedback.iconActive;
    }
    if (feedback.icon) {
      return feedback.icon;
    }
    
    // Fallback to local rating assets with active states
    switch (feedback.value) {
      case 1: return isSelected ? faceSadActive : faceSad;
      case 2: return isSelected ? faceNeutralActive : faceNeutral;
      case 3: return isSelected ? faceSmileActive : faceSmile;
      case 4: return isSelected ? faceHappyActive : faceHappy;
      default: return isSelected ? faceSadActive : faceSad;
    }
  };

  // Check if this is NPS (11 items: 0-10)
  const isNPS = feedbacks.length === 11;

  // Split NPS into two rows: 0-5 and 6-10
  const firstRow = isNPS ? feedbacks.slice(0, 6) : feedbacks;
  const secondRow = isNPS ? feedbacks.slice(6) : [];

  return (
    <div className="add-review-form-container">
      {/* Rating Section */}
      <div className="add-review-rating-section">

        {/* Rating Grid - First Row */}
        <div className={`add-review-rating-grid ${isNPS ? 'nps-grid' : ''}`}>
          {firstRow.map((feedback) => {
            const isSelected = selectedFeedback?.value === feedback.value;
            return (
              <button
                key={feedback.value}
                className={`add-review-rating-button ${isNPS ? 'nps-button' : ''} ${isSelected ? 'selected' : ''}`}
                onClick={() => onSelectFeedback(feedback)}
              >
                <div className="add-review-rating-content">
                  {!isNPS && (
                    <div className="add-review-rating-icon">
                      <img
                        src={getIconSrc(feedback, isSelected)}
                        alt={feedback.name}
                      />
                    </div>
                  )}
                  <span className={`add-review-rating-label-text ${isNPS ? 'nps-label' : ''}`}>{feedback.name}</span>
                </div>
              </button>
            );
          })}
        </div>

        {/* Rating Grid - Second Row (NPS only) */}
        {isNPS && secondRow.length > 0 && (
          <div className="add-review-rating-grid nps-grid">
            {secondRow.map((feedback) => {
              const isSelected = selectedFeedback?.value === feedback.value;
              return (
                <button
                  key={feedback.value}
                  className={`add-review-rating-button nps-button ${isSelected ? 'selected' : ''}`}
                  onClick={() => onSelectFeedback(feedback)}
                >
                  <div className="add-review-rating-content">
                    <span className="add-review-rating-label-text nps-label">{feedback.name}</span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Submit Button */}
      <div className="add-review-action-buttons">
        <button 
          onClick={onSubmit} 
          disabled={!selectedFeedback || loading}
          className="add-review-button add-review-button-primary"
        >
          {loading ? 'Submitting...' : 'Submit and continue'}
        </button>
      </div>
    </div>
  );
};

export default AddReviewForm; 